import styled from 'react-emotion';
import { ThemeHelper } from '../../styles/theme-helper';
import {
    ICON_AND_TEXT,
    ICON_ONLY,
    LOZENGE,
    SMALL,
    LARGE,
    RIGHT
} from './constants';

const defaultValues = (theme) => {
    const { typography, layout } = theme;
    return {
        size: {
            small: {
                icon: 12,
                deltaX: 1,
                deltaY: 0.75
            },
            medium: {
                icon: 20,
                deltaX: 2,
                deltaY: 1
            },
            large: {
                icon: 28,
                deltaX: 2.75,
                deltaY: 1.5
            }
        },
        fontSize: typography.size,
        lineHeight: typography.lineHeight,
        borderRadius: layout.borderRadius,
        spacing: layout.spacing,
        boxShadow: 'none',
        minWidth: 'auto' // applies to buttons with text only
    };
};

function getOverrides(theme) {
    return ThemeHelper.getComponentOverrides(theme, 'button', defaultValues);
}

const getColours = (props, palette, typography, inverse) => {
    let bgColour = '#fff';
    let borderColour = '#fff';
    let textColour = typography.colour;

    if (props.primary) {
        bgColour = palette.primary.main;
        borderColour = palette.primary.dark;
        textColour = palette.primary.text;
    } else if (props.danger) {
        bgColour = palette.error.light;
        borderColour = palette.error.main;
        textColour = palette.error.text;
    } else if (props.info) {
        bgColour = palette.secondary.light;
        borderColour = palette.secondary.main;
        textColour = palette.secondary.text;
    }

    if (inverse) {
        return {
            bgColour: textColour,
            borderColour: textColour,
            textColour: borderColour
        };
    }
    return { bgColour, borderColour, textColour };
};

const getSize = (buttonSize, theme, hasSpinner) => {
    if (hasSpinner) {
        return '18px';
    }
    const { size } = getOverrides(theme);
    if (buttonSize === SMALL) {
        return `${size.small.icon}px`;
    } else if (buttonSize === LARGE) {
        return `${size.large.icon}px`;
    }
    return `${size.medium.icon}px`;
};

const getPadding = (buttonSize, buttonState, theme) => {
    const { size, spacing } = getOverrides(theme);
    let { deltaX, deltaY } = size.medium;
    if (buttonSize === SMALL) {
        ({ deltaX, deltaY } = size.small);
    } else if (buttonSize === LARGE) {
        ({ deltaX, deltaY } = size.large);
    }

    let padding = `${spacing * deltaY}px ${spacing * deltaX}px`;
    if (buttonState === ICON_ONLY) {
        padding = 0;
    } else if (buttonState === ICON_AND_TEXT) {
        padding = `${spacing * deltaY * 0.5}px ${spacing * deltaX}px`;
    }

    return padding;
};

const getIconPadding = (buttonSize, buttonState, alignIcon, hasSpinner) => {
    if (hasSpinner) {
        return 0;
    }
    let delta = 1;
    if (buttonSize === SMALL) {
        delta = 0.75;
    } else if (buttonSize === LARGE) {
        delta = 1.5;
    }

    if (buttonState === ICON_ONLY) {
        return `${4 * delta}px`;
    }
    if (alignIcon === RIGHT) {
        return `${4 * delta}px 0 ${4 * delta}px ${8 * delta}px`;
    }
    return `${4 * delta}px ${8 * delta}px ${4 * delta}px 0`;
};

const getTextStyles = (buttonSize, theme) => {
    let { fontSize, lineHeight } = getOverrides(theme);
    if (buttonSize === SMALL) {
        fontSize = `${parseInt(fontSize, 10) - 2}px`;
        lineHeight = `${parseInt(fontSize, 10) - 2}px`;
    } else if (buttonSize === LARGE) {
        fontSize = `${parseInt(fontSize, 10) + 2}px`;
        lineHeight = `${parseInt(fontSize, 10) + 2}px`;
    }

    return { fontSize, lineHeight };
};

const getMinWidth = (buttonSize, baseMinWidth) => {
    let minWidth = parseInt(baseMinWidth, 10);
    if (Number.isNaN(minWidth)) {
        return baseMinWidth;
    }

    if (buttonSize === SMALL) {
        minWidth *= 0.5;
    } else if (buttonSize === LARGE) {
        minWidth *= 1.25;
    }
    return `${minWidth}px`;
};

const ButtonWrapper = styled('div')((props) => {
    const theme = ThemeHelper.getTheme(props);
    const { palette, typography } = theme;
    const { borderRadius, boxShadow, minWidth } = getOverrides(theme);
    const { buttonState, size, hasSpinner, disabled } = props;

    const { bgColour, borderColour, textColour } = getColours(
        props,
        palette,
        typography,
        props.inverse
    );
    const padding = getPadding(size, buttonState, theme);
    const { fontSize, lineHeight } = getTextStyles(size, theme);
    const btnBorderRadius =
        props.type === LOZENGE ? getSize(size, theme) : borderRadius;
    const btnMinWidth =
        props.buttonState !== ICON_ONLY
            ? `min-width: ${getMinWidth(size, minWidth)};`
            : '';
    return `
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-family: ${typography.family};
    font-size: ${fontSize};
    line-height: ${lineHeight};
    text-align: center;
    border-radius: ${btnBorderRadius};
    background-color: ${bgColour};
    border: 2px solid ${borderColour};
    box-sizing: border-box;
    color: ${textColour};
    padding: ${padding};
    box-shadow: ${boxShadow};
    transition: filter 0.2s;
    ${btnMinWidth}

    &:not(.disable):hover {
      filter: brightness(1.1);
    }

    ${(disabled || hasSpinner) &&
    `cursor: not-allowed;
      box-shadow: none;
      opacity: 0.65;

      &:focus {
          outline: 0;
      }

      &:hover {
        filter: none;
      }
    `}
  `;
});

export const Content = styled('div')((props) => {
    const { hasSpinner } = props;
    const margin = hasSpinner ? 'margin: 0 10px;' : '';
    return `
        display: flex;
        align-items: center;
        ${margin}
    `;
});

const Icon = styled('div')((props) => {
    const theme = ThemeHelper.getTheme(props);
    const { size, buttonState, alignIcon, hasSpinner } = props;
    const iconSize = getSize(size, theme, hasSpinner);
    // const pathFill = hasSpinner ? 'unset !important' : 'currentColor !important';
    const spinAnimation = hasSpinner
        ? `
    animation-name: spin;
    animation-duration: 1000ms;
    animation-iteration-count: infinite;
    animation-timing-function: linear;

    @keyframes spin {
    from {
        transform:rotate(0deg);
    }
    to {
        transform:rotate(360deg);
    }
  `
        : '';
    return `
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${iconSize};
    width: ${iconSize};
    padding: ${getIconPadding(size, buttonState, alignIcon, hasSpinner)};

    > span {
        height: ${iconSize} !important;
        width: ${iconSize} !important;
    }
    ${spinAnimation}
  `;
});

export { ButtonWrapper, Icon };



// WEBPACK FOOTER //
// ./components/button/styled.js